import { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { Lock, LockFill } from 'react-bootstrap-icons';
import {
  buildBlocks,
  bundleGraftLocations,
  bundleMeasuredValues,
  calendarDate,
  graftOptionLabel,
  isFieldVisible,
  repeatRowCount,
  splitOptions,
} from '../../compass/compassModel';

function titleCase(value) {
  return String(value || '')
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((word) => {
      if (/[A-Z]/.test(word.slice(1))) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

function displayLabel(field) {
  const label = field.field_label || '';
  const level = label.match(/:\s*level (from|to)$/i);
  if (level) return `Level ${level[1].toLowerCase()}`;
  return label;
}

function bareKey(field) {
  return String(field.field_key || '').replace(/#\d+$/, '');
}

function deviceFillIn(field) {
  return /^alliance\.(?:device|mycase)\./.test(bareKey(field));
}

function NaButton({ onChange }) {
  return (
    <button type="button" className="compass-na" onClick={() => onChange('N/A')}>
      N/A
    </button>
  );
}

function companionStem(field) {
  return bareKey(field).replace(/_(level_from|level_to|start_date|end_date|packs|packs_period)$/i, '');
}

function heavilyRelated(left, right) {
  const leftKey = bareKey(left);
  const rightKey = bareKey(right);
  if (rightKey === `${leftKey}_unit` || rightKey === `${leftKey}_units`) return true;
  const leftStem = companionStem(left);
  const rightStem = companionStem(right);
  return leftStem !== leftKey && leftStem === rightStem;
}

function fieldStem(key) {
  return String(key || '').replace(
    /_(clinically_relevant|description|why_not_excluded|level_from|level_to|start_date|specify|presence|location)$/i,
    '',
  );
}

function isFollowUpOf(parent, field) {
  if (!parent || !/^If /i.test(field.applies_when || '')) return false;
  const parentKey = parent.field_key || '';
  const key = field.field_key || '';
  if (key.startsWith(`${parentKey}_`) || key.startsWith(`${parentKey}.`)) return true;
  const stem = fieldStem(key);
  return stem !== key && stem === fieldStem(parentKey);
}

function clusterFields(fields, nest) {
  if (!nest) return fields.map((field) => ({ parent: field, followUps: [] }));
  const clusters = [];
  let current = null;
  fields.forEach((field) => {
    if (current && isFollowUpOf(current.parent, field)) {
      current.followUps.push(field);
      return;
    }
    current = { parent: field, followUps: [] };
    clusters.push(current);
  });
  return clusters;
}

function layoutGroups(fields, nest) {
  const groups = [];
  clusterFields(fields, nest).forEach((cluster) => {
    const last = groups[groups.length - 1];
    const previous = last?.clusters[last.clusters.length - 1];
    const canPair = previous
      && !previous.followUps.length
      && !cluster.followUps.length
      && heavilyRelated(previous.parent, cluster.parent);
    if (canPair) {
      last.layout = 'related';
      last.clusters.push(cluster);
      return;
    }
    groups.push({ layout: 'line', clusters: [cluster] });
  });
  return groups;
}

function hintFor(field) {
  const when = field.applies_when || '';
  if (!when || /^(Always|Pre-op|Each |Every |Index |When radiographs|Study period|End of participation|Optional)/i.test(when)) {
    return '';
  }
  if (when.length > 140) return `${when.slice(0, 137)}…`;
  return when;
}

function ScoreOptions({ id, options, value, onChange }) {
  return (
    <div className="compass-options" role="radiogroup" aria-label={id}>
      {options.map((option) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          className={value === option ? 'is-selected' : ''}
          onClick={() => onChange(option)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

function entryChars(field, options, narrative) {
  const type = field.data_type;
  const key = `${field.field_key || ''} ${field.field_label || ''}`.toLowerCase();
  let chars = 18;
  if (type === 'integer' || type === 'decimal' || type === 'number') chars = 4;
  else if (type === 'time') chars = 12;
  else if (type === 'month') chars = 13;
  else if (type === 'date') chars = 15;
  else if (type === 'datetime') chars = 20;
  else if ((type === 'single_select' || type === 'integer') && options.length) {
    const longest = options.reduce((max, option) => Math.max(max, option.length), 6);
    chars = Math.min(Math.max(longest, 6), 36);
  } else if (narrative || /address|street|comment|description|reason|summary|information/.test(key)) chars = 42;
  else if (/level from|level to|level_from|level_to|\bzip\b|postal|site identifying|subject number|subject id|version|criterion id|\bcode\b|lot|serial|catalog/.test(key)) chars = 8;
  else if (/printed name|\bname\b|medication|product|device|city|email|phone|investigator|surgeon/.test(key)) chars = 28;
  else if (/specify|other/.test(key)) chars = 32;
  return chars + 5;
}

function FieldControl({ field, value, onChange, inputId, highlight = false }) {
  const options = splitOptions(field.response_options);
  const hint = hintFor(field);
  const label = displayLabel(field);
  const medicationEvent = /^alliance\.med\.ae_[123](?:#\d+)?$/.test(String(field.field_key || ''));
  const narrative = field.data_type === 'text'
    && /description|specify|comment|summary|reason|information|diagnosis|procedure|term/i.test(field.field_label || '');
  const wideText = narrative || medicationEvent;
  const shortScore = options.length > 0
    && options.length <= 6
    && options.every((option) => option.length <= 16)
    && (field.data_type === 'single_select' || field.data_type === 'integer');
  const namedGroup = field.data_type === 'multi_select' || shortScore
    || (field.data_type === 'single_select' && options.length > 0 && options.length <= 5);
  const entry = {
    className: 'compass-entry',
    style: { '--entry': entryChars(field, options, narrative) },
  };

  const numeric = field.data_type === 'integer' || field.data_type === 'decimal' || field.data_type === 'number';
  const buttonChoices = shortScore || (field.data_type === 'single_select' && options.length > 0 && options.length <= 5);
  const deviceEntry = deviceFillIn(field);
  let control = null;
  if (bareKey(field) === 'alliance.visit.timepoint') {
    control = (
      <Form.Control id={inputId} className={entry.className} style={entry.style} type="text" value={value || ''} readOnly />
    );
  } else if (value === 'N/A' && (numeric || buttonChoices) && !(deviceEntry && numeric)) {
    control = (
      <Form.Control id={inputId} className={entry.className} style={entry.style} type="text" value="N/A" readOnly />
    );
  } else if (field.data_type === 'boolean' || field.data_type === 'checkbox') {
    control = (
      <Form.Check
        id={inputId}
        type="checkbox"
        checked={value === true}
        onChange={(event) => onChange(event.target.checked)}
      />
    );
  } else if (field.data_type === 'multi_select') {
    const selected = Array.isArray(value) ? value : (value && value !== 'N/A' ? [value] : []);
    control = (
      <div className="compass-choice-grid" role="group" aria-labelledby={`${inputId}-label`}>
        {options.map((option) => (
          <Form.Check
            key={option}
            id={`${inputId}-${option}`}
            type="checkbox"
            label={option}
            checked={selected.includes(option)}
            onChange={(event) => {
              const next = event.target.checked
                ? [...selected, option]
                : selected.filter((item) => item !== option);
              onChange(next);
            }}
          />
        ))}
      </div>
    );
  } else if (shortScore) {
    control = <ScoreOptions id={label} options={options} value={value || ''} onChange={onChange} />;
  } else if (field.data_type === 'single_select' && options.length > 0 && options.length <= 5) {
    control = (
      <div role="radiogroup" aria-labelledby={`${inputId}-label`}>
        {options.map((option) => (
          <Form.Check
            key={option}
            id={`${inputId}-${option}`}
            type="radio"
            name={inputId}
            label={option}
            checked={value === option}
            onChange={() => onChange(option)}
          />
        ))}
      </div>
    );
  } else if ((field.data_type === 'single_select' || field.data_type === 'integer') && options.length > 0) {
    const listed = value === 'N/A' && !options.includes('N/A') ? [...options, 'N/A'] : options;
    control = (
      <Form.Select id={inputId} className={entry.className} style={entry.style} value={value || ''} onChange={(event) => onChange(event.target.value)}>
        <option value="">Select</option>
        {listed.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </Form.Select>
    );
  } else if (value === 'N/A' && (field.data_type === 'date' || field.data_type === 'month' || field.data_type === 'time' || field.data_type === 'datetime')) {
    control = (
      <Form.Control id={inputId} className={entry.className} style={entry.style} type="text" value="N/A" readOnly />
    );
  } else if (field.data_type === 'date' || field.data_type === 'month' || field.data_type === 'time' || field.data_type === 'datetime') {
    const documentDate = /document_date$/.test(bareKey(field));
    const type = field.data_type === 'month'
      ? 'month'
      : documentDate
        ? 'date'
        : field.data_type === 'datetime'
          ? 'datetime-local'
          : field.data_type;
    const shown = type === 'date' ? calendarDate(value) : (value || '');
    control = (
      <Form.Control
        id={inputId}
        className={entry.className}
        style={entry.style}
        type={type}
        value={shown}
        onChange={(event) => onChange(event.target.value)}
        onBlur={(event) => {
          const next = type === 'date' ? calendarDate(event.target.value) : event.target.value;
          if (next && next !== shown) onChange(next);
        }}
      />
    );
  } else if (field.data_type === 'integer' || field.data_type === 'decimal' || field.data_type === 'number') {
    const derived = String(field.field_key || '').replace(/#\d+$/, '') === 'alliance.baseline.bmi';
    const marked = value === 'N/A';
    control = (
      <span className="compass-inline-value">
        <Form.Control
          id={inputId}
          className={entry.className}
          style={entry.style}
          type={marked ? 'text' : 'number'}
          step={marked || field.data_type === 'integer' ? '1' : 'any'}
          value={marked ? 'N/A' : (value || '')}
          readOnly={derived}
          onChange={(event) => onChange(event.target.value)}
        />
        {field.unit ? <span className="compass-unit">{field.unit}</span> : null}
        {derived ? <span className="compass-unit">kg/m²</span> : null}
        {deviceEntry ? <NaButton onChange={onChange} /> : null}
      </span>
    );
  } else if (wideText) {
    control = (
      <div className="compass-comment">
        <Form.Control
          id={inputId}
          className={entry.className}
          style={entry.style}
          as={narrative ? 'textarea' : undefined}
          rows={narrative ? 3 : undefined}
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
        />
        <NaButton onChange={onChange} />
      </div>
    );
  } else {
    const input = (
      <Form.Control
        id={inputId}
        className={entry.className}
        style={entry.style}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
      />
    );
    control = deviceEntry ? (
      <div className="compass-comment">
        {input}
        <NaButton onChange={onChange} />
      </div>
    ) : input;
  }

  const question = namedGroup ? (
    <span className="compass-question" id={`${inputId}-label`}>{label}</span>
  ) : (
    <label className="compass-question" htmlFor={inputId}>{label}</label>
  );

  return (
    <div className={`compass-field${wideText || field.data_type === 'multi_select' ? ' is-wide' : ''}`}>
      {question}
      <div className={`compass-answer${highlight ? ' is-next' : ''}`}>{control}</div>
      {hint && field.data_type !== 'boolean' ? <span className="compass-hint">{hint}</span> : null}
    </div>
  );
}

function listedFields(fields, nest) {
  const entries = [];
  layoutGroups(fields, nest).forEach((group) => {
    group.clusters.forEach((cluster) => {
      entries.push(cluster.parent);
      cluster.followUps.forEach((field) => entries.push(field));
    });
  });
  return entries;
}

function GraftLocationField({ group, values, onChange, idPrefix, highlight = false }) {
  const other = group.options.find((field) => /\.graft_other(#\d+)?$/.test(field.field_key));
  const otherOn = Boolean(other && values[other.field_key] === true);
  const highlighted = highlight && (
    highlight === group.field_key
    || group.options.some((field) => field.field_key === highlight)
  );

  const markNa = () => {
    if (group.specify && values[group.specify.field_key] !== 'N/A') onChange(group.specify.field_key, 'N/A');
  };

  const toggle = (field, checked) => {
    const isOther = /\.graft_other(#\d+)?$/.test(field.field_key);
    onChange(field.field_key, checked);
    if (!group.specify) return;
    if (isOther && checked) {
      if (values[group.specify.field_key] === 'N/A') onChange(group.specify.field_key, '');
      return;
    }
    if (isOther || !otherOn) markNa();
  };

  return (
    <div className="compass-field is-wide">
      <span className="compass-question" id={`${idPrefix}-${group.field_key}-label`}>Location of bone graft</span>
      <div className={`compass-answer${highlighted ? ' is-next' : ''}`}>
        <div className="compass-choice-grid" role="group" aria-labelledby={`${idPrefix}-${group.field_key}-label`}>
          {group.options.map((field) => (
            <Form.Check
              key={field.field_key}
              id={`${idPrefix}-${field.field_key}`}
              type="checkbox"
              label={graftOptionLabel(field)}
              checked={values[field.field_key] === true}
              onChange={(event) => toggle(field, event.target.checked)}
            />
          ))}
        </div>
      </div>
      {otherOn && group.specify ? (
        <label className="compass-graft-specify" htmlFor={`${idPrefix}-${group.specify.field_key}`}>
          <span>Other, specify</span>
          <span className="compass-inline-value">
            <Form.Control
              id={`${idPrefix}-${group.specify.field_key}`}
              value={values[group.specify.field_key] || ''}
              onChange={(event) => onChange(group.specify.field_key, event.target.value)}
            />
            {deviceFillIn(group.specify) ? (
              <NaButton onChange={(next) => onChange(group.specify.field_key, next)} />
            ) : null}
          </span>
        </label>
      ) : null}
    </div>
  );
}

function unitChoiceLabel(option) {
  if (String(option).toLowerCase() === 'lb') return 'lbs';
  return option;
}

function MeasureField({ group, values, onChange, idPrefix, highlight = false }) {
  const field = group.field;
  const options = splitOptions(group.unit.response_options);
  const inputId = `${idPrefix}-${field.field_key}`;
  const highlighted = highlight === field.field_key || highlight === group.unit.field_key;
  const entry = { '--entry': 9 };
  return (
    <div className="compass-field">
      <label className="compass-question" htmlFor={inputId}>{displayLabel(field)}</label>
      <div className={`compass-answer${highlighted ? ' is-next' : ''}`}>
        <span className="compass-measure">
          <Form.Control
            id={inputId}
            className="compass-entry"
            style={entry}
            type="number"
            step={field.data_type === 'integer' ? '1' : 'any'}
            value={values[field.field_key] || ''}
            onChange={(event) => onChange(field.field_key, event.target.value)}
          />
          <span className="compass-unit-choices" role="radiogroup" aria-label={displayLabel(group.unit)}>
            {options.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={values[group.unit.field_key] === option}
                className={values[group.unit.field_key] === option ? 'is-selected' : ''}
                onClick={() => onChange(group.unit.field_key, option)}
              >
                {unitChoiceLabel(option)}
              </button>
            ))}
          </span>
        </span>
      </div>
    </div>
  );
}

function questionRows(fields, values) {
  return bundleMeasuredValues(bundleGraftLocations(fields)).flatMap((item) => {
    if (item.kind === 'measure') {
      return isFieldVisible(item.field, values) ? [item] : [];
    }
    if (item.kind !== 'graft-location') return isFieldVisible(item, values) ? [item] : [];
    const options = item.options.filter((field) => isFieldVisible(field, values));
    if (!options.length) return [];
    return [{ ...item, options }];
  });
}

function FieldRows({ fields, values, onChange, idPrefix, nest = true, highlightFieldKey = '' }) {
  const entries = questionRows(listedFields(fields, nest), values);
  return (
    <div className="compass-field-rows">
      {entries.map((item) => (
        item.kind === 'graft-location' ? (
          <div key={item.field_key} className="compass-field-row" id={`${idPrefix}-row-${item.field_key}`}>
            <GraftLocationField
              group={item}
              values={values}
              onChange={onChange}
              idPrefix={idPrefix}
              highlight={highlightFieldKey}
            />
          </div>
        ) : item.kind === 'measure' ? (
          <div key={item.field_key} className="compass-field-row" id={`${idPrefix}-row-${item.field_key}`}>
            <MeasureField
              group={item}
              values={values}
              onChange={onChange}
              idPrefix={idPrefix}
              highlight={highlightFieldKey}
            />
          </div>
        ) : (
          <div key={item.field_key} className="compass-field-row" id={`${idPrefix}-row-${item.field_key}`}>
            <FieldControl
              field={item}
              inputId={`${idPrefix}-${item.field_key}`}
              value={values[item.field_key]}
              onChange={(next) => onChange(item.field_key, next)}
              highlight={highlightFieldKey === item.field_key}
            />
          </div>
        )
      ))}
    </div>
  );
}

function headingKey(value) {
  return titleCase(value).toLowerCase();
}

function Section({ title, children, className = '', showTitle = true }) {
  const sectionClass = ['compass-section', className].filter(Boolean).join(' ');
  return (
    <section className={sectionClass}>
      {showTitle ? <h3>{titleCase(title)}</h3> : null}
      {children}
    </section>
  );
}

function RepeatCard({ group, values, onChange, repeatState, onRepeatCount, idPrefix, highlightFieldKey = '', showTitle = true }) {
  const rows = repeatRowCount(group, values, repeatState);
  const maxRaw = group.items[0]?.max_repeats;
  const max = maxRaw === 'repeating' ? 30 : Number(maxRaw) || 10;
  const counted = group.headers.some((field) => field.field_key.endsWith('_count'));
  const title = group.id.replace(/_/g, ' ');
  const headers = group.headers.filter((field) => isFieldVisible(field, values));

  return (
    <Section title={title} className="compass-repeat" showTitle={showTitle}>
      {headers.length > 0 && (
        <FieldRows
          fields={headers}
          values={values}
          onChange={onChange}
          idPrefix={idPrefix}
          highlightFieldKey={highlightFieldKey}
        />
      )}
      {Array.from({ length: rows }, (_, index) => (
        <div className="compass-repeat-row" key={`${group.id}-${index}`}>
          <FieldRows
            fields={group.items.map((field) => {
              const key = `${field.field_key}#${index}`;
              const label = rows > 1 ? `${field.field_label} (${index + 1})` : field.field_label;
              return { ...field, field_key: key, field_label: label };
            })}
            values={values}
            onChange={onChange}
            idPrefix={idPrefix}
            nest={false}
            highlightFieldKey={highlightFieldKey}
          />
        </div>
      ))}
      {!counted && (
        <div className="compass-repeat-actions">
          <button type="button" className="compass-text-button" onClick={() => onRepeatCount(group.id, rows + 1)} disabled={rows >= max}>
            Add another
          </button>
          {rows > 1 && (
            <button type="button" className="compass-text-button" onClick={() => onRepeatCount(group.id, rows - 1)}>
              Remove last
            </button>
          )}
        </div>
      )}
    </Section>
  );
}

export function FormNavbar({
  phase,
  title,
  onWhatsNext = null,
  missingOn = false,
  missingLocked = false,
  onToggleMissingLock = null,
  children = null,
}) {
  return (
    <div className="compass-form-nav">
      <div className="compass-form-nav-phase">{titleCase(phase)}</div>
      <h2 className="compass-form-title">{titleCase(title)}</h2>
      <div className="compass-form-nav-actions">
        {children}
        {onWhatsNext && (
          <span className="compass-missing">
            <button
              type="button"
              className="compass-text-button"
              data-whats-missing=""
              aria-pressed={missingOn}
              onClick={onWhatsNext}
            >
              What's missing?
            </button>
            <button
              type="button"
              className="compass-missing-lock"
              data-whats-missing=""
              aria-pressed={missingLocked}
              aria-label={missingLocked ? "Unlock What's missing" : "Lock What's missing"}
              title={missingLocked ? "Unlock What's missing" : "Stay on What's missing"}
              onClick={onToggleMissingLock || undefined}
            >
              {missingLocked ? <LockFill size={11} /> : <Lock size={11} />}
            </button>
          </span>
        )}
      </div>
    </div>
  );
}

function visibleRepeat(block, repeats, values) {
  const group = repeats.get(block.id);
  if (!group) return null;
  const visibleGroup = {
    ...group,
    headers: group.headers.filter((field) => isFieldVisible(field, values)),
    items: group.items.filter((field) => (
      /\.graft_other_specify(?:#\d+)?$/.test(field.field_key || '') || isFieldVisible(field, values)
    )),
  };
  if (!visibleGroup.headers.length && !visibleGroup.items.length) return null;
  return visibleGroup;
}

export default function CompassForm({
  fields,
  values,
  onChange,
  repeatState,
  onRepeatCount,
  idPrefix,
  highlightFieldKey = '',
  formTitle = '',
}) {
  const { blocks, repeats } = useMemo(() => buildBlocks(fields), [fields]);
  const visibleBlocks = blocks.flatMap((block) => {
    if (block.type === 'repeat') {
      const group = visibleRepeat(block, repeats, values);
      return group ? [{ block, group, title: group.id.replace(/_/g, ' ') }] : [];
    }
    const visible = block.fields.filter((field) => isFieldVisible(field, values));
    return visible.length ? [{ block, visible, title: block.section }] : [];
  });
  const formKey = headingKey(formTitle);
  const loneHeading = visibleBlocks.length <= 1;

  return (
    <div>
      {visibleBlocks.map((entry) => {
        const sameAsForm = Boolean(formKey) && headingKey(entry.title) === formKey;
        const showTitle = !loneHeading && !sameAsForm;
        if (entry.group) {
          return (
            <RepeatCard
              key={entry.block.id}
              group={entry.group}
              values={values}
              onChange={onChange}
              repeatState={repeatState}
              onRepeatCount={onRepeatCount}
              idPrefix={idPrefix}
              highlightFieldKey={highlightFieldKey}
              showTitle={showTitle}
            />
          );
        }
        return (
          <Section
            key={`${entry.block.section}-${entry.visible[0].field_key}`}
            title={entry.block.section}
            showTitle={showTitle}
          >
            <FieldRows
              fields={entry.visible}
              values={values}
              onChange={onChange}
              idPrefix={idPrefix}
              highlightFieldKey={highlightFieldKey}
            />
          </Section>
        );
      })}
    </div>
  );
}
